import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { fetchCampaignDetails, CampaignDetails } from '../apiClient/fetchCampaignData';
import MapDataButton from '../components/MapDataButton';
import DownloadCsv from '../components/DownloadCsv';
import DownloadRawJson from '../components/DownloadRawJson';
import MapVizModal from '../components/MapVizModal';
import { DataTable, DataTableRef } from '../components/DataTable';

const CampaignDetailPage = () => {
    const { campaignId } = useParams<{ campaignId: string }>();
    const [campaign, setCampaign] = useState<CampaignDetails | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const [showMap, setShowMap] = useState(false);
    const [selectedNetworkType, setSelectedNetworkType] = useState<string>('');
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const dataTableRef = React.useRef<DataTableRef>(null);

    useEffect(() => {
        if (campaignId) {
            setLoading(true);
            fetchCampaignDetails(campaignId)
                .then(data => {
                    setCampaign(data);
                    setLoading(false);
                })
                .catch(err => {
                    setError('Failed to fetch campaign details.');
                    setLoading(false);
                });
        }
    }, [campaignId]);

    const center: [number, number] = [41.759103, -87.1];
    const zoom = 10;
    const width = "100%";
    const height = "1000px";

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="mt-5">
                <Alert variant="danger">{error}</Alert>
            </Container>
        );
    }

    if (!campaign) {
        return (
            <Container className="mt-5">
                <Alert variant="warning">Campaign not found.</Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            {campaignId && showMap && (
                <MapVizModal
                    showMap={showMap}
                    setShowMap={setShowMap}
                    campaignId={campaignId}
                    selectedNetworkType={selectedNetworkType}
                    center={center}
                    zoom={zoom}
                    width={width}
                    height={height}
                />
            )}
            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header as="h1">{campaign.name}</Card.Header>
                        <Card.Body>
                            <Card.Text>{campaign.description}</Card.Text>
                            <Row>
                                <Col md={6}>
                                    <strong>ID:</strong> {campaign.id} <br />
                                    <strong>Code:</strong> {campaign.code} <br />
                                    <strong>Owner:</strong> {campaign.owner || 'N/A'}
                                </Col>
                                <Col md={6}>
                                    <strong>Created At:</strong> {new Date(campaign.created_at).toLocaleString()} <br />
                                    <strong>Facilitator:</strong> {campaign.facilitator || 'N/A'} <br />
                                    <strong>Access Level:</strong> {campaign.access_level}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="mb-4">
                <Col>
                    <Card>
                        <Card.Header as="h2">Actions</Card.Header>
                        <Card.Body className="d-flex flex-wrap gap-2">
                            <MapDataButton
                                campaignId={campaignId}
                                setShowMap={setShowMap}
                                setSelectedNetworkType={setSelectedNetworkType}
                                disabled={!campaignId}
                            />
                            <DownloadCsv
                                campaignId={campaignId}
                                selectedNetworkType={selectedNetworkType}
                                disabled={!campaignId}
                            />
                            <DownloadRawJson
                                campaignId={campaignId}
                                selectedNetworkType={selectedNetworkType}
                                disabled={!campaignId}
                            />
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col>
                    <Card>
                        <Card.Header as="h2">Collections</Card.Header>
                        <Card.Body>
                            {campaignId ? (
                                <DataTable
                                    ref={dataTableRef}
                                    setSelectedRows={setSelectedRows}
                                    campaignId={campaignId}
                                    enableRowSelection={false}
                                    enableCustomSelectAll={false}
                                />
                            ) : (
                                <Alert variant="info">No campaign ID found.</Alert>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default CampaignDetailPage;
